import React from 'react';
import Icon from '../../../components/AppIcon';

const fmtOMR = (v) =>
  v == null || v === 0
    ? null
    : `OMR ${Number(v)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const HighlightRow = ({ icon, iconColor, iconBg, label, value, sub, badge, badgeColor }) => {
  const hasValue = value !== null && value !== undefined && value !== '';
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-background/60 border border-border/50 hover:border-border transition-all duration-150 hover:shadow-sm">
      <div className={`${iconBg} rounded-xl p-2 flex-shrink-0`}>
        <Icon name={icon} size={15} className={iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-muted-foreground font-medium mb-0.5">{label}</div>
        <div className="text-sm font-semibold text-foreground truncate">
          {hasValue ? value : <span className="text-muted-foreground font-normal italic text-xs">No Data Available</span>}
        </div>
      </div>
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        {sub && hasValue && (
          <span className="text-xs font-semibold text-muted-foreground">{sub}</span>
        )}
        {badge && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badgeColor}`}>{badge}</span>
        )}
      </div>
    </div>
  );
};

const MonthlyHighlights = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5, 6, 7, 8]?.map(i => (
          <div key={i} className="h-[58px] bg-muted/30 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const highlights = [
    // ── Project Statistics ──
    {
      icon: 'FolderOpen',
      iconColor: 'text-sky-600',
      iconBg: 'bg-sky-100 dark:bg-sky-900/40',
      label: 'Total Projects This Month',
      value: data?.totalProjects != null ? String(data?.totalProjects) : null,
      sub: null,
      badge: data?.totalProjects > 0 ? `${data?.totalProjects} record${data?.totalProjects !== 1 ? 's' : ''}` : null,
      badgeColor: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
    },
    {
      icon: 'PlayCircle',
      iconColor: 'text-indigo-600',
      iconBg: 'bg-indigo-100 dark:bg-indigo-900/40',
      label: 'Projects In Progress',
      value: data?.inProgressProjects != null ? String(data?.inProgressProjects) : null,
      sub: null,
      badge: data?.inProgressProjects > 0 ? 'Active' : (data?.inProgressProjects === 0 ? 'None' : null),
      badgeColor: data?.inProgressProjects > 0
        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' :'bg-muted text-muted-foreground',
    },
    {
      icon: 'CheckCircle',
      iconColor: 'text-emerald-600',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
      label: 'Completed Projects',
      value: data?.completedProjects != null ? String(data?.completedProjects) : null,
      sub: fmtOMR(data?.totalRevenue),
      badge: null,
      badgeColor: '',
    },
    // ── Business Highlights ──
    {
      icon: 'Trophy',
      iconColor: 'text-amber-600',
      iconBg: 'bg-amber-100 dark:bg-amber-900/40',
      label: 'Largest Project / Deal',
      value: data?.largestDeal?.name || null,
      sub: fmtOMR(data?.largestDeal?.value),
      badge: data?.largestDeal?.client || null,
      badgeColor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    },
    {
      icon: 'Star',
      iconColor: 'text-sky-600',
      iconBg: 'bg-sky-100 dark:bg-sky-900/40',
      label: 'Top Customer of the Month',
      value: data?.topCustomer || null,
      sub: fmtOMR(data?.topCustomerRevenue),
      badge: null,
      badgeColor: '',
    },
    {
      icon: 'Building2',
      iconColor: 'text-emerald-600',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
      label: 'Best Performing Department',
      value: data?.topDepartment || null,
      sub: fmtOMR(data?.topDepartmentRevenue),
      badge: null,
      badgeColor: '',
    },
    {
      icon: 'AlertTriangle',
      iconColor: data?.delayedProjects > 0 ? 'text-red-600' : 'text-emerald-600',
      iconBg: data?.delayedProjects > 0 ? 'bg-red-100 dark:bg-red-900/40' : 'bg-emerald-100 dark:bg-emerald-900/40',
      label: 'Delayed Projects',
      value: data?.delayedProjects != null ? String(data?.delayedProjects) : null,
      sub: null,
      badge: data?.delayedProjects > 0 ? 'Needs Attention' : 'All On Schedule',
      badgeColor: data?.delayedProjects > 0
        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    {
      icon: 'User',
      iconColor: 'text-violet-600',
      iconBg: 'bg-violet-100 dark:bg-violet-900/40',
      label: 'Top Sales Performer',
      value: data?.topSalesPerson || null,
      sub: fmtOMR(data?.topSalesValue),
      badge: null,
      badgeColor: '',
    },
  ];

  const hasAnyData = data?.totalProjects > 0 || highlights?.some(h => h?.value !== null && h?.value !== undefined && h?.value !== '');

  if (!data || (!hasAnyData && data?.totalProjects === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-3">
        <div className="w-12 h-12 rounded-full bg-muted/30 flex items-center justify-center">
          <Icon name="BarChart2" size={20} className="opacity-40" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium">No Data Available</p>
          <p className="text-xs text-muted-foreground/60 mt-0.5">No projects recorded for this period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {highlights?.map((item, idx) => (
        <HighlightRow key={idx} {...item} />
      ))}
    </div>
  );
};

export default MonthlyHighlights;
